import { createAccessControl } from "better-auth/plugins/access";
import { defaultStatements, userAc as defaultUserAc } from "better-auth/plugins/admin/access";

/**
 * §5 Nymbl Staff roles — the single source of truth for role literals and the
 * Better Auth admin-plugin access control. Shared by the server config
 * (@/lib/auth) and the client SDK config (@/lib/auth-client) so both sides
 * agree on what a valid role is. This module must stay free of any db/server
 * dependency (unlike @/lib/auth) so it can be imported from client code.
 */
export const NYMBL_ROLES = ["ADMIN", "RM", "SA", "ACCT", "FIN"] as const;
export type NymblRole = (typeof NYMBL_ROLES)[number];

/**
 * Narrow an unknown role string to a NymblRole. The boundary guard for anything that
 * reads `user.role` — a value that fails this is not a role the permission matrix knows,
 * so it must be refused (401) rather than treated as some default.
 */
export function isNymblRole(value: string | null | undefined): value is NymblRole {
  return typeof value === "string" && (NYMBL_ROLES as readonly string[]).includes(value);
}

export const nymblAc = createAccessControl(defaultStatements);

/** The admin-plugin user powers ADMIN and RM share — everything except impersonation. */
const SHARED_ADMIN_USER_ACTIONS = [
  "create",
  "list",
  "set-role",
  "ban",
  "delete",
  "set-password",
  "get",
  "update",
] as const;

/**
 * ADMIN alone may View As (impersonate) — any user, other ADMINs and RMs included, which
 * is what `impersonate-admins` grants. Product decision 2026-10-19; see @/lib/view-as.
 */
const nymblAdminAc = nymblAc.newRole({
  user: [...SHARED_ADMIN_USER_ACTIONS, "impersonate", "impersonate-admins"],
  session: ["list", "revoke", "delete"],
});

/** RM keeps user management but cannot View As (removed 2026-10-19 — it was never used). */
const nymblRmAc = nymblAc.newRole({
  user: [...SHARED_ADMIN_USER_ACTIONS],
  session: ["list", "revoke", "delete"],
});

export const nymblRoleAccess = {
  ADMIN: nymblAdminAc,
  RM: nymblRmAc,
  SA: defaultUserAc,
  ACCT: defaultUserAc,
  FIN: defaultUserAc,
};
